import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import { AuthContext } from '../../App';
import ActiveSessionsCard from './ActiveSessionsCard';
import AccountSecurityMFA from './AccountSecurityMFA';
import SubscriptionTab from '../SubscriptionTab';
import LoginHistoryCard from './LoginHistoryCard';
import { useConfirm } from '../ConfirmProvider';

function AccountTab({ fetchWithAuth, setActiveTab }) {
  const confirm = useConfirm();
  const { user, token, logout, changePassword, refreshUser } = React.useContext(AuthContext);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showDeleteConfirm1, setShowDeleteConfirm1] = useState(false);
  const [showDeleteConfirm2, setShowDeleteConfirm2] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleLogout = async () => {
    if (await confirm({
      title: 'Log out of this device?',
      message: 'You will be signed out here. Any other devices you are signed in on will stay signed in.',
      confirmLabel: 'Log Out',
    })) {
      await logout(true);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');

    try {
      const result = await changePassword(
        passwordForm.currentPassword,
        passwordForm.newPassword,
        passwordForm.confirmPassword
      );

      if (result.success) {
        alert('Password changed successfully! You will now be logged out and need to sign in with your new password.');
        setShowPasswordForm(false);
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPasswordError(result.error);
      }
    } catch (error) {
      setPasswordError('An unexpected error occurred. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Clear all local data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        alert('Your account has been permanently deleted.');
        window.location.href = '/';
      } else {
        let errorMessage = 'Failed to delete account';
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
        } catch {
          errorMessage = `Server error: ${response.status}`;
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      alert('Error deleting account. Please try again.');
    }
  };

  const handlePasswordFormChange = (e) => {
    setPasswordForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Account Management</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>
        <div className="page-nav-bar"></div>
      </div>

      <div className="page-scroll-content">
      {/* User Information Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Account Information</h3>
        </div>
        <div className="p-5">
          <div className="mb-3">
            <strong>Email:</strong> {user?.email || 'Not available'}
          </div>
          <div className="mb-3">
            <strong>Username:</strong> {user?.username || 'Not available'}
          </div>
          <div className="mb-3">
            <strong>Name:</strong> {`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Not set'}
          </div>
          <div className="mb-3">
            <strong>Plan:</strong> {(() => {
              // Single source — backend effective_tier (#consistency P0/A). No FE recompute.
              const names = { base: 'Healist Base', pro: 'Healist Pro', max: 'Healist Max' };
              if (user?.permanent_test_acct) return 'Test Account (Max)';
              if (user?.subscription_status === 'trialing') return `${names[user?.subscription_tier] || 'Healist'} (Trial)`;
              return names[user?.effective_tier] || 'Free';
            })()}
          </div>
          <div className="mb-3">
            <strong>Account Status:</strong> {user?.permanent_test_acct ? 'Active (Test)' : (user?.subscription_status || 'trial').charAt(0).toUpperCase() + (user?.subscription_status || 'trial').slice(1)}
          </div>
          <div className="mb-3">
            <strong>Member Since:</strong> {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication (Phase 2) */}
      <AccountSecurityMFA />

      {/* Active Sessions Card */}
      <ActiveSessionsCard />

      {/* Login History Card */}
      <LoginHistoryCard fetchWithAuth={fetchWithAuth} />

      {/* Account Actions Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Account Actions</h3>
        </div>
        <div className="p-5">
          <div className="acc-actionGroup">
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="btn btn-secondary acc-pwdToggleBtn"

            >
              {showPasswordForm ? 'Cancel Password Change' : 'Change Password'}
            </button>

            <button
              onClick={handleLogout}
              className="btn btn-danger acc-logoutBtn"

            >
              Logout This Device
            </button>

            <button
              onClick={() => setShowDeleteConfirm1(true)}
              className="acc-deleteBtn"
            >
              Delete Account
            </button>

          </div>

          <div className="acc-logoutNote">
            <strong>Note:</strong> "Logout This Device" will end your session on this device only.
            Other devices will remain signed in. To sign out everywhere, use
            "Logout All Other Devices" above, then log out here.
          </div>
        </div>
      </div>

      {/* Password Change Form */}
      {showPasswordForm && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Change Password</h3>
          </div>
          <div className="p-5">
            <form onSubmit={handlePasswordChange}>
              {passwordError && (
                <div className="acc-pwdError">
                  {passwordError}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Current Password</label>
                <div className="acc-pwdInputWrap">
                  <input
                    type={showCurrentPw ? "text" : "password"}
                    name="currentPassword"
                    className="form-input"
                    value={passwordForm.currentPassword}
                    onChange={handlePasswordFormChange}
                    required
                  />
                  <button type="button" className="acc-pwdEye" onClick={() => setShowCurrentPw(!showCurrentPw)} title={showCurrentPw ? "Hide" : "Show"}>
                    {showCurrentPw ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">New Password</label>
                <div className="acc-pwdInputWrap">
                  <input
                    type={showNewPw ? "text" : "password"}
                    name="newPassword"
                    className="form-input"
                    value={passwordForm.newPassword}
                    onChange={handlePasswordFormChange}
                    required
                    minLength={12}
                  />
                  <button type="button" className="acc-pwdEye" onClick={() => setShowNewPw(!showNewPw)} title={showNewPw ? "Hide" : "Show"}>
                    {showNewPw ? "🙈" : "👁️"}
                  </button>
                </div>
                <small className="acc-pwdRequirement">
                  Must be at least 12 characters with uppercase, lowercase, digit, and special character. Cannot reuse last 5 passwords.
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <div className="acc-pwdInputWrap">
                  <input
                    type={showConfirmPw ? "text" : "password"}
                    name="confirmPassword"
                    className="form-input"
                    value={passwordForm.confirmPassword}
                    onChange={handlePasswordFormChange}
                    required
                  />
                  <button type="button" className="acc-pwdEye" onClick={() => setShowConfirmPw(!showConfirmPw)} title={showConfirmPw ? "Hide" : "Show"}>
                    {showConfirmPw ? "🙈" : "👁️"}
                  </button>
                </div>
              </div>

              <div className="acc-pwdButtonGroup">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="acc-pwdChangeBtn" style={{ cursor: passwordLoading ? 'not-allowed' : 'pointer' }}
                >
                  {passwordLoading ? 'Changing Password...' : 'Change Password'}
                </button>

                <button
                  type="button"
                  onClick={() => setShowPasswordForm(false)}
                  className="acc-pwdCancelBtn"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription & Payment Management */}
      <SubscriptionTab fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} user={user} refreshUser={refreshUser} embedded={true} />

      {/* ACCOUNT DELETE CONFIRMATION - 1st MODAL WARNING */}
      {showDeleteConfirm1 && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-sm">
            <div className="modal-body">
            <h3 className="modal-title modal-title-danger">
              URGENT - Account Deletion Warning
            </h3>
            <p className="modal-subtitle mb-4">
              You are about to completely <strong>DELETE your entire account and ALL DATA</strong> associated with your account!
            </p>
            <p className="modal-subtitle mb-4">
              This action is <strong>irreversible</strong>! If you wish to rejoin you will need to create a brand new account and start over as a NEW user.
            </p>
            <p className="text-lg font-semibold text-error mb-5">
              Are you sure you want to do this?
            </p>
            <div className="modal-footer">
              <button
                className="btn btn-secondary flex-1"
                onClick={() => setShowDeleteConfirm1(false)}
              >
                CANCEL
              </button>
              <button
                className="btn btn-danger flex-1"
                onClick={() => {
                  setShowDeleteConfirm1(false);
                  setShowDeleteConfirm2(true);
                }}
              >
                YES - DELETE
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* ACCOUNT DELETE CONFIRMATION - FINAL MODAL WARNING */}
      {showDeleteConfirm2 && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-xs modal-content-danger">
            <div className="modal-body">
            <h3 className="modal-title modal-title-danger modal-title-center">
              FINAL CONFIRMATION
            </h3>
            <p className="text-center font-semibold mb-5">
              Please confirm you wish to execute<br/>
              IRREVERSIBLE ACCOUNT DELETION
            </p>
            <div className="modal-footer">
              <button
                className="btn btn-primary flex-1"
                onClick={() => {
                  setShowDeleteConfirm2(false);
                  setShowDeleteConfirm1(false);
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger flex-1"
                onClick={() => {
                  setShowDeleteConfirm2(false);
                  handleDeleteAccount();
                }}
              >
                Confirm
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default AccountTab;
